import { store } from '../../../../../app/store'
import { fieldMapperApi } from '../../../../../features/fieldMapper/fieldMapperApi'
import { mappingsApi, METHOD_SEGMENT, SALES_PERSON_SEGMENT } from '../../../../../features/mappings/mappingsApi'

/**
 * Promise helpers for GraphContext, which chains its requests imperatively.
 * They resolve to { data } and reject with an error carrying response.status,
 * the shape GraphContext was written against, so its logic stays unchanged.
 * Auth headers are added by the shared RTK baseQuery.
 */
const run = async (request) => {
    try {
        return { data: await request.unwrap() }
    } catch (error) {
        const status = error && error.status
        const failure = new Error(typeof status === 'number' ? `Request failed with status ${status}` : 'Request failed')
        if (typeof status === 'number') {
            failure.response = { status, data: error.data }
        }
        throw failure
    } finally {
        if (typeof request.reset === 'function') {
            request.reset()
        }
    }
}

const query = (endpoint, arg) => run(store.dispatch(endpoint.initiate(arg, { subscribe: false, forceRefetch: true })))
const mutate = (endpoint, arg) => run(store.dispatch(endpoint.initiate(arg)))

const { endpoints } = fieldMapperApi

export const getSubscription = (subscriptionId) => query(endpoints.getSubscription, subscriptionId)

/** body: { endpointId, subscriptionId, entityName, isForceRefresh } */
export const getSchema = (body) => mutate(endpoints.getSchema, body)

export const getMarketplaceEndpoints = () => query(endpoints.getMarketplaceEndpoints, undefined)

/** body: { endpointId, subscriptionId, entityName } */
export const getMapping = (body) => mutate(endpoints.getMapping, body)

/** body: { type, subscriptionId, mappingSchema, entityName } */
export const saveMapping = (body) => mutate(endpoints.saveMapping, body)

export const getShippingMappings = (connectorId, instanceId) =>
    query(endpoints.getMethodMappingList, { resource: 'shipping', connectorId, instanceId })

export const saveShippingMappings = (connectorId, instanceId, mappings) =>
    mutate(mappingsApi.endpoints.saveMethodMappings, { resource: 'shipping', connectorId, instanceId, mappings })

export const getPaymentMappings = (connectorId, instanceId) =>
    query(endpoints.getMethodMappingList, { resource: 'payment', connectorId, instanceId })

export const savePaymentMappings = (connectorId, instanceId, mappings) =>
    mutate(mappingsApi.endpoints.saveMethodMappings, { resource: 'payment', connectorId, instanceId, mappings })

/** Sales person mappings are scoped to the connection, not the instance. */
export const getSalesPersonMappings = (connectorId) =>
    query(endpoints.getSalesPersonMappingList, { connectorId })

/** Resolves to { data: { result, message, id } }. */
export const saveSalesPersonMappings = (connectorId, mappings) =>
    mutate(mappingsApi.endpoints.saveSalesPersonMappings, { connectorId, mappings })

/**
 * Loads the values both systems offer for a virtual entity and resolves to
 * { data: { erp: [{ value, label }], ecom: [...] } }. Never rejects: a side that
 * fails to load is logged and comes back empty, so saved mappings still render.
 */
const bothSides = (label, loadSide) =>
    Promise.all(['erp', 'ecommerce'].map((side) =>
        loadSide(side)
            .then((response) => response.data)
            .catch((error) => {
                console.error(`Failed to load ${side} ${label} values:`, error)
                return []
            })
    )).then(([erp, ecom]) => ({ data: { erp, ecom } }))

const getMethodOptions = (resource, connectorId, instanceId) =>
    bothSides(resource, (side) =>
        query(endpoints.getMethodOptions, { resource, connectorId, instanceId, segment: METHOD_SEGMENT[side] }))

export const getShippingOptions = (connectorId, instanceId) => getMethodOptions('shipping', connectorId, instanceId)

export const getPaymentOptions = (connectorId, instanceId) => getMethodOptions('payment', connectorId, instanceId)

/** Options carry the system's salesPersonId as `id`, which the save payload needs. */
export const getSalesPersonOptions = (connectorId) =>
    bothSides('sales person', (side) =>
        query(endpoints.getSalesPersonOptions, { connectorId, segment: SALES_PERSON_SEGMENT[side] }))
